import { Toastr } from 'components/global/Toastr'
import { handleResponseErr } from 'utils/apiHelperUtils'
import { addHours, format } from 'date-fns'
import { Action } from 'types/redux'
import * as actions from './actions'

type PayrunType = {
  attendance_review_request_id: string
  attendance_review_status: string
  confirmed_at: string
  cut_off_from_date: string
  cut_off_to_date: string
  cutoff_end_date: string
  cutoff_start_date: string
  employees_count: string
  id: string
  draft_payrun: string
  month: string
  month_year_i18n: string
  pay_period_end: string
  pay_period_start: string
  payrun_id: string
  remaining_days: number
  required_close_previous_payruns: boolean
  status: string
  status_i18n: string
  year: string
}

export type InitialStateType = {
  // ? temp
  current_payrun: PayrunType
  fetching: boolean
  payrunMonth: string
  payrun_approval_cycle: {
    employee_id: number
    followup_needed: boolean
    review_request_id: number
  }
  payruns: {
    date: string
    id: number
    month_year_i18n: string
  }[]
  previous_payruns: {
    closed_by: {
      full_name_i18n: string
    }
    closing_date: string
    cutoff_end_date: string
    cutoff_start_date: string
    id: string
    month_year_i18n: string
    pay_period_end: string
    pay_period_start: string
    payrun_status: string
    payrun_status_i18n: string
    required_close_previous_payruns: boolean
    status: string
    status_i18n: string
    total_net_salaries: string
  }[]
  reminder_last_updated: string | null
  sending: boolean
}

const initialState: InitialStateType = {
  current_payrun: {
    attendance_review_request_id: '',
    attendance_review_status: '',
    confirmed_at: '',
    cut_off_from_date: '',
    cut_off_to_date: '',
    cutoff_end_date: '',
    cutoff_start_date: '',
    draft_payrun: '',
    employees_count: '',
    id: '',
    remaining_days: -1,
    required_close_previous_payruns: false,
    month: '',
    month_year_i18n: '',
    pay_period_end: '',
    pay_period_start: '',
    payrun_id: '',
    status: '',
    status_i18n: '',
    year: '',
  },
  previous_payruns: [],
  payrun_approval_cycle: {
    employee_id: 0,
    followup_needed: false,
    review_request_id: 0,
  },
  fetching: false,
  sending: false,
  reminder_last_updated: localStorage.getItem('payrun-reminder') || null,
  payruns: [],
  payrunMonth: '',
}

export default function reducer(state = initialState, action: Action): InitialStateType {
  switch (action.type) {
    case actions.PAYROLL_PAYRUN_LOAD_PENDING:
    case actions.PAYROLL_PAYRUN_CANCEL_PENDING:
    case actions.CURRENT_PAYRUN_DETAIL_LOAD_PENDING:
    case actions.PAYRUNS_LOAD_PENDING:
    case actions.PAYRUN_SEND_ATTENDANCE_REVIW_REQUESTS_PENDING:
    case actions.PREVIOUS_PAYRUNS_PENDING:
    case actions.SKIP_PAYRUNS_PENDING:
    case actions.START_PAYRUNS_PENDING:
    case actions.PREVIOUS_PERIODS_PAYRUNS_PENDING:
    case actions.REOPEN_PAYRUNS_PENDING:
    case actions.PAYROLL_PAYRUN_CONFIRM_PENDING: {
      return {
        ...state,
        fetching: true,
      }
    }
    case actions.PAYROLL_PAYRUN_LOAD_FULFILLED:
      return {
        ...state,
        ...action.payload.data.data,
        fetching: false,
      }
    case actions.PAYROLL_PAYRUN_CONFIRM_FULFILLED:
    case actions.PAYROLL_PAYRUN_CANCEL_FULFILLED:
    case actions.PREVIOUS_PERIODS_PAYRUNS_FULFILLED:
    case actions.SKIP_PAYRUNS_FULFILLED:
    case actions.START_PAYRUNS_FULFILLED:
    case actions.REOPEN_PAYRUNS_FULFILLED:
    case actions.PAYRUN_SEND_ATTENDANCE_REVIW_REQUESTS_FULFILLED:
      Toastr.success(action.payload.data.message)
      return {
        ...state,
        fetching: false,
      }
    case actions.PAYROLL_PAYRUN_LOAD_REJECTED:
    case actions.PAYROLL_PAYRUN_CONFIRM_REJECTED:
    case actions.PAYROLL_PAYRUN_CANCEL_REJECTED:
    case actions.PAYRUNS_LOAD_REJECTED:
    case actions.CURRENT_PAYRUN_DETAIL_LOAD_REJECTED:
      Toastr.error(handleResponseErr(action.payload))
      return {
        ...state,
        fetching: false,
      }

    case actions.PAYRUN_SEND_REMINDER_PENDING: {
      return {
        ...state,
        sending: true,
      }
    }

    case actions.PAYRUN_SEND_REMINDER_FULFILLED: {
      const last_updated = addHours(new Date(), 2).toString()
      localStorage.setItem('payrun-reminder', last_updated)
      return {
        ...state,
        sending: false,
        reminder_last_updated: last_updated,
      }
    }
    case actions.PAYRUNS_LOAD_FULFILLED:
      return {
        ...state,
        ...action.payload.data.data,
        fetching: false,
      }

    case actions.PREVIOUS_PAYRUNS_FULFILLED:
      return {
        ...state,
        previous_payruns: action.payload.data.data,
        fetching: false,
      }
    case actions.CURRENT_PAYRUN_DETAIL_LOAD_FULFILLED:
      return {
        ...state,
        current_payrun: action.payload.data.data,
        payrun_approval_cycle: action.payload.data.data.payroll_approval_cycle,
        fetching: false,
        payrunMonth: format(
          new Date(action.payload?.data?.data?.year, action.payload?.data?.data?.month - 1, 1),
          'MMMM yyyy',
        ),
      }
    case actions.PAYRUN_DETAIL_LOAD_FULFILLED:
      return {
        ...state,
        current_payrun: action.payload.data.data.payrun,
        payrun_approval_cycle: action.payload.data.data.payrun.payroll_approval_cycle,
        fetching: false,
        payrunMonth: format(
          new Date(
            action.payload?.data?.data?.payrun?.year,
            action.payload?.data?.data?.payrun?.month - 1,
            1,
          ),
          'MMMM yyyy',
        ),
      }
    case actions.PREVIOUS_PAYRUNS_REJECTED:
    case actions.PAYRUN_SEND_REMINDER_REJECTED:
    case actions.PREVIOUS_PERIODS_PAYRUNS_REJECTED:
    case actions.SKIP_PAYRUNS_REJECTED:
    case actions.REOPEN_PAYRUNS_REJECTED:
    case actions.START_PAYRUNS_REJECTED:
    case actions.PAYRUN_SEND_ATTENDANCE_REVIW_REQUESTS_REJECTED:
      Toastr.error(handleResponseErr(action.payload))
      return {
        ...state,
        fetching: false,
      }

    default:
      return state
  }
}
